import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from '../App';
import { isExpired, eventDDay } from '../utils/datetime';

interface MoPeer {
 moPeerHash: string;
 name?: string;
 email: string;
 phone?: string;
 businessRegistrationNumber?: string;
}

interface MoFamily {
 moFamilyHash: string;
 name: string;
 description?: string;
 ownerMoPeerHash?: string;
 ownerName?: string;
 ownerEmail?: string;
 ownerPhone?: string;
 ownerBusinessNumber?: string;
}

interface VideoFile {
 file_hash: string;
 title: string;
 thumbnail_path?: string;
 file_type: string;
 upload_date: string;
 event_start_at?: string | null; // ISO — 행사 시작 시각(미래면 D-N 뱃지)
 expires_at?: string | null;     // ISO — 라이프타임 만료 시각(지나면 dimmed)
 isDeleted?: boolean; // 삭제되었거나 내 목록에 없는 영상
}

interface CampaignFormData {
 title: string;
 isActive: boolean;
 advertiserMoPeerHash?: string;
 advertiserBusinessNumber?: string;
 advertiserName: string;
 advertiserEmail?: string;
 advertiserPhone?: string;
 moFamilyHash: string;
 startDatetime: string;
 endDatetime: string;
 budget: number;
 videos: string[];
}

const CampaignForm: React.FC = () => {
 const navigate = useNavigate();
 const { campaignHash } = useParams();
 const isEdit = !!campaignHash;
 const { user } = useAppStore();

 const [formData, setFormData] = useState<CampaignFormData>({
 title: '',
 isActive: true,
 advertiserMoPeerHash: '',
 advertiserBusinessNumber: '',
 advertiserName: '',
 advertiserEmail: '',
 advertiserPhone: '',
 moFamilyHash: '',
 startDatetime: '',
 endDatetime: '',
 budget: 0,
 videos: []
 });

 const [isLoading, setIsLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 
 // 검색 관련 상태
 const [moPeerSearch, setMoPeerSearch] = useState('');
 const [moPeerResults, setMoPeerResults] = useState<MoPeer[]>([]);
 const [selectedMoPeer, setSelectedMoPeer] = useState<MoPeer | null>(null);
 
 const [moFamilySearch, setMoFamilySearch] = useState('');
 const [moFamilyResults, setMoFamilyResults] = useState<MoFamily[]>([]);
 const [selectedMoFamily, setSelectedMoFamily] = useState<MoFamily | null>(null);
 
 const [myVideos, setMyVideos] = useState<VideoFile[]>([]);
 const [deletedVideos, setDeletedVideos] = useState<VideoFile[]>([]); // 삭제된 영상
 const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
 const [slotCounts, setSlotCounts] = useState<Record<string, number>>({});
 const [totalSlots, setTotalSlots] = useState(0);

 // 기존 캠페인 로드 (수정 모드)
 useEffect(() => {
 if (isEdit && campaignHash) {
 loadCampaign();
 }
 }, [isEdit, campaignHash]);

 // 내 영상 목록 로드
 useEffect(() => {
 loadMyVideos();
 }, []);
 
 // 내 영상 목록이 로드된 후 삭제된 영상 확인
 useEffect(() => {
 if (isEdit && campaignHash && myVideos.length > 0 && selectedVideos.size > 0) {
 loadCampaignVideos(Array.from(selectedVideos));
 }
 }, [myVideos, isEdit, campaignHash]);

 const loadCampaign = async () => {
 try {
 const response = await fetch(`/campaign/${campaignHash}`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 const campaign = data.data;
 console.log('Loaded campaign data:', campaign); // 디버깅용
 
 setFormData({
 title: campaign.title,
 isActive: campaign.isActive,
 advertiserMoPeerHash: campaign.advertiserMoPeerHash || '',
 advertiserBusinessNumber: campaign.advertiserBusinessNumber || '',
 advertiserName: campaign.advertiserName,
 advertiserEmail: campaign.advertiserEmail || '',
 advertiserPhone: campaign.advertiserPhone || '',
 moFamilyHash: campaign.moFamilyHash,
 startDatetime: formatDatetimeForInput(campaign.startDatetime),
 endDatetime: formatDatetimeForInput(campaign.endDatetime),
 budget: campaign.budget,
 videos: campaign.videos || []
 });
 
 setSelectedVideos(new Set(campaign.videos || []));
 
 // 모피어 정보 설정
 if (campaign.advertiserMoPeerHash) {
 setSelectedMoPeer({
 moPeerHash: campaign.advertiserMoPeerHash,
 name: campaign.advertiserName,
 email: campaign.advertiserEmail || '',
 phone: campaign.advertiserPhone || '',
 businessRegistrationNumber: campaign.advertiserBusinessNumber || ''
 });
 setMoPeerSearch(campaign.advertiserName || campaign.advertiserEmail || '');
 }
 
 // 모패밀리 정보 설정
 if (campaign.moFamilyHash && campaign.moFamilyName) {
 setSelectedMoFamily({
 moFamilyHash: campaign.moFamilyHash,
 name: campaign.moFamilyName
 });
 setMoFamilySearch(campaign.moFamilyName);
 }
 } else {
 setError('캠페인 정보를 불러오는데 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 로드 오류:', error);
 setError('캠페인 정보를 불러오는데 실패했습니다.');
 }
 };

 const loadMyVideos = async () => {
 try {
 const response = await fetch('/mofile/my-files', {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setMyVideos(data.data || []);
 }
 } catch (error) {
 console.error('영상 목록 로드 오류:', error);
 }
 };

 // 캠페인에 있는 영상들 정보 로드
 const loadCampaignVideos = async (videoHashes: string[]) => {
 const missingVideos: VideoFile[] = [];
 const myVideoHashes = new Set(myVideos.map(v => v.file_hash));
 
 // 내 영상 목록에 없는 영상들만 확인
 const missingHashes = videoHashes.filter(hash => !myVideoHashes.has(hash));
 
 for (const fileHash of missingHashes) {
 try {
 const response = await fetch(`/mofile/${fileHash}/info`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 if (data.data) {
 missingVideos.push({
 file_hash: fileHash,
 title: data.data.title || '삭제된 영상',
 thumbnail_path: data.data.thumbnail_path,
 file_type: data.data.file_type || 'video/mp4',
 upload_date: data.data.created_at || '',
 isDeleted: true
 });
 }
 } else {
 // 영상을 찾을 수 없는 경우
 missingVideos.push({
 file_hash: fileHash,
 title: '삭제된 영상',
 file_type: 'video/mp4',
 upload_date: '',
 isDeleted: true
 });
 }
 } catch (error) {
 console.error(`Failed to load video ${fileHash}:`, error);
 missingVideos.push({
 file_hash: fileHash,
 title: '삭제된 영상',
 file_type: 'video/mp4',
 upload_date: '',
 isDeleted: true
 });
 }
 }
 
 setDeletedVideos(missingVideos);
 };

 // 날짜 형식 변환 (datetime-local input용)
 const formatDatetimeForInput = (dateString: string) => {
 const date = new Date(dateString);
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const hours = String(date.getHours()).padStart(2, '0');
 const minutes = String(date.getMinutes()).padStart(2, '0');
 return `${year}-${month}-${day}T${hours}:${minutes}`;
 };

 // 모피어 검색
 const searchMoPeer = async (query: string) => {
 if (query.length < 2) {
 setMoPeerResults([]);
 return;
 }

 try {
 const response = await fetch(`/mopeer/search?q=${encodeURIComponent(query)}`, {
 credentials: 'include',
 headers: {
 'Accept': 'application/json',
 'Content-Type': 'application/json'
 }
 });
 
 const contentType = response.headers.get('content-type');
 console.log('Response content-type:', contentType);
 
 if (response.ok) {
 if (contentType && contentType.includes('application/json')) {
 const data = await response.json();
 setMoPeerResults(data.data || []);
 } else {
 console.error('예상치 못한 응답 타입:', contentType);
 const text = await response.text();
 console.error('응답 내용:', text);
 }
 } else {
 console.error('모피어 검색 응답 오류:', response.status, response.statusText);
 const text = await response.text();
 console.error('응답 내용:', text);
 }
 } catch (error) {
 console.error('모피어 검색 오류:', error);
 }
 };

 // 모패밀리 검색
 const searchMoFamily = async (query: string) => {
 try {
 const response = await fetch(`/campaign/search/mofamily?q=${encodeURIComponent(query)}`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setMoFamilyResults(data.data || []);
 }
 } catch (error) {
 console.error('모패밀리 검색 오류:', error);
 }
 };

 // 모피어 선택
 const selectMoPeer = (moPeer: MoPeer) => {
 setSelectedMoPeer(moPeer);
 setFormData({
 ...formData,
 advertiserMoPeerHash: moPeer.moPeerHash,
 advertiserBusinessNumber: moPeer.businessRegistrationNumber || formData.advertiserBusinessNumber,
 advertiserName: moPeer.name || formData.advertiserName,
 advertiserEmail: moPeer.email || formData.advertiserEmail,
 advertiserPhone: moPeer.phone || formData.advertiserPhone
 });
 setMoPeerSearch(moPeer.name || moPeer.email);
 setMoPeerResults([]);
 };

 // 모패밀리 선택
 const selectMoFamily = (moFamily: MoFamily) => {
 setSelectedMoFamily(moFamily);
 setFormData({
 ...formData,
 moFamilyHash: moFamily.moFamilyHash
 });
 setMoFamilySearch(moFamily.name);
 setMoFamilyResults([]);
 
 // 선택한 영상들의 슬롯 수 계산
 if (selectedVideos.size > 0) {
 calculateSlots(moFamily.moFamilyHash, Array.from(selectedVideos));
 }
 };

 // 영상 선택/해제
 const toggleVideo = (fileHash: string) => {
 const newSelectedVideos = new Set(selectedVideos);
 if (newSelectedVideos.has(fileHash)) {
 newSelectedVideos.delete(fileHash);
 } else {
 newSelectedVideos.add(fileHash);
 }
 setSelectedVideos(newSelectedVideos);
 setFormData({
 ...formData,
 videos: Array.from(newSelectedVideos)
 });
 
 // 슬롯 수 재계산
 if (formData.moFamilyHash) {
 calculateSlots(formData.moFamilyHash, Array.from(newSelectedVideos));
 }
 };

 // 슬롯 수 계산
 const calculateSlots = async (moFamilyHash: string, videos: string[]) => {
 if (!moFamilyHash || videos.length === 0) {
 setSlotCounts({});
 setTotalSlots(0);
 return;
 }

 try {
 const response = await fetch('/campaign/calculate-slots', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ moFamilyHash, videos })
 });
 
 if (response.ok) {
 const data = await response.json();
 setSlotCounts(data.data.slotCounts);
 setTotalSlots(data.data.totalSlots);
 }
 } catch (error) {
 console.error('슬롯 수 계산 오류:', error);
 }
 };

 // 폼 제출
 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 
 if (!formData.title || !formData.advertiserName || !formData.moFamilyHash || 
 !formData.startDatetime || !formData.endDatetime || formData.budget === undefined) {
 setError('필수 입력 항목을 모두 입력해주세요.');
 return;
 }

 setIsLoading(true);
 setError(null);

 try {
 const url = isEdit ? `/campaign/${campaignHash}` : '/campaign/create';
 const method = isEdit ? 'PUT' : 'POST';
 
 const response = await fetch(url, {
 method,
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({
 ...formData,
 videos: Array.from(selectedVideos)
 })
 });

 if (response.ok) {
 navigate('/campaign');
 } else {
 const data = await response.json();
 setError(data.error || '캠페인 저장에 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 저장 오류:', error);
 setError('캠페인 저장에 실패했습니다.');
 } finally {
 setIsLoading(false);
 }
 };

 return (
 <div className="max-w-4xl mx-auto">
 <div className="mb-6">
 <h1 className="text-2xl font-bold">{isEdit ? '캠페인 수정' : '새 캠페인 만들기'}</h1>
 </div>

 {error && (
 <div className="bg-red-50 text-red-800 p-4 mb-4">
 {error}
 </div>
 )}

 <form onSubmit={handleSubmit} className="bg-white shadow p-6 space-y-6">
 {/* 활성화 상태 */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">현재 활성화 여부</label>
 <div className="flex gap-4">
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 value="true"
 checked={formData.isActive}
 onChange={() => setFormData({ ...formData, isActive: true })}
 className="mr-2"
 />
 활성
 </label>
 <label className="flex items-center">
 <input
 type="radio"
 name="isActive"
 value="false"
 checked={!formData.isActive}
 onChange={() => setFormData({ ...formData, isActive: false })}
 className="mr-2"
 />
 비활성
 </label>
 </div>
 </div>

 {/* 캠페인 제목 */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 제목 <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 required
 />
 </div>

 {/* 광고주 정보 섹션 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">광고주 정보</h3>
 
 {/* 모피어 검색 */}
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">
 모피어 (옵션)
 </label>
 <div className="relative">
 <input
 type="text"
 value={moPeerSearch}
 onChange={(e) => {
 setMoPeerSearch(e.target.value);
 searchMoPeer(e.target.value);
 }}
 placeholder="이름, 이메일, 전화번호로 검색"
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 {moPeerResults.length > 0 && (
 <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-auto">
 {moPeerResults.map((moPeer) => (
 <button
 key={moPeer.moPeerHash}
 type="button"
 onClick={() => selectMoPeer(moPeer)}
 className="w-full text-left px-3 py-2 hover:bg-gray-100"
 >
 <div className="font-medium">{moPeer.name || '이름 없음'}</div>
 <div className="text-sm text-gray-500">{moPeer.email}</div>
 </button>
 ))}
 </div>
 )}
 </div>
 </div>

 {/* 광고주 필드들 */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 담당자 이름 <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={formData.advertiserName}
 onChange={(e) => setFormData({ ...formData, advertiserName: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 required
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 사업자 등록번호 (옵션)
 </label>
 <input
 type="text"
 value={formData.advertiserBusinessNumber}
 onChange={(e) => setFormData({ ...formData, advertiserBusinessNumber: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 이메일 (옵션)
 </label>
 <input
 type="email"
 value={formData.advertiserEmail}
 onChange={(e) => setFormData({ ...formData, advertiserEmail: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 전화번호 (옵션)
 </label>
 <input
 type="tel"
 value={formData.advertiserPhone}
 onChange={(e) => setFormData({ ...formData, advertiserPhone: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 </div>
 </div>

 {/* 캠페인 대상 섹션 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">캠페인 대상</h3>
 
 {/* 모패밀리 검색 */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 캠페인 대상 모패밀리 <span className="text-red-500">*</span>
 </label>
 <div className="relative">
 <input
 type="text"
 value={moFamilySearch}
 onChange={(e) => {
 setMoFamilySearch(e.target.value);
 searchMoFamily(e.target.value);
 }}
 onFocus={() => searchMoFamily('')}
 placeholder="캠페인 대상 모패밀리 검색"
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 {moFamilyResults.length > 0 && (
 <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-auto">
 {moFamilyResults.map((moFamily) => (
 <button
 key={moFamily.moFamilyHash}
 type="button"
 onClick={() => selectMoFamily(moFamily)}
 className="w-full text-left px-3 py-2 hover:bg-gray-100"
 >
 <div className="font-medium">{moFamily.name}</div>
 {moFamily.ownerName && (
 <div className="text-sm text-gray-500">소유주: {moFamily.ownerName}</div>
 )}
 </button>
 ))}
 </div>
 )}
 </div>
 {selectedMoFamily && (
 <div className="mt-2 text-sm text-gray-600">
 선택됨: {selectedMoFamily.name}
 </div>
 )}
 </div>

 {/* 영상 선택 */}
 <div className="mt-6">
 <label className="block text-sm font-medium text-gray-700 mb-2">
 영상 선택
 </label>
 
 {/* 삭제된 영상 섹션 */}
 {deletedVideos.length > 0 && (
 <div className="mb-4">
 <div className="text-sm text-red-600 mb-2">캠페인에 포함되어 있지만 더 이상 사용할 수 없는 영상</div>
 <div className="border border-red-300 p-4 bg-red-50">
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
 {deletedVideos.map((video) => (
 <div
 key={video.file_hash}
 className={`relative cursor-pointer border-2 p-2 ${
 selectedVideos.has(video.file_hash)
 ? 'border-red-500 bg-red-100'
 : 'border-red-200 hover:border-red-300'
 }`}
 onClick={() => toggleVideo(video.file_hash)}
 >
 <div className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1">
 삭제됨
 </div>
 {video.thumbnail_path ? (
 <img
 src={`/uploads/thumbnails/${video.thumbnail_path}`}
 alt={video.title}
 className="w-full h-24 object-cover opacity-50"
 />
 ) : (
 <div className="w-full h-24 bg-gray-200 flex items-center justify-center opacity-50">
 <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
 </svg>
 </div>
 )}
 <div className="mt-1 text-xs truncate text-red-600">
 {video.title}
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 )}
 
 {/* 내 영상 목록 */}
 {myVideos.length === 0 ? (
 <div className="text-gray-500">내 영상이 없습니다.</div>
 ) : (
 <div className="border border-gray-300 p-4 max-h-96 overflow-y-auto">
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
 {myVideos.map((video) => {
 const expired = isExpired(video.expires_at);
 const dday = expired ? null : eventDDay(video.event_start_at);
 return (
 <div
 key={video.file_hash}
 className={`relative cursor-pointer border-2 p-2 ${expired ? 'opacity-50 ' : ''}${
 selectedVideos.has(video.file_hash)
 ? 'border-blue-500 bg-blue-50'
 : 'border-gray-200 hover:border-gray-300'
 }`}
 onClick={() => toggleVideo(video.file_hash)}
 title={expired ? '만료됨 — 재생 안 됨' : undefined}
 >
 {expired ? (
 <div className="absolute top-1 left-1 z-10 bg-gray-700 text-white text-xs px-1 py-0.5">만료</div>
 ) : dday != null ? (
 <div className="absolute top-1 left-1 z-10 bg-blue-600 text-white text-xs px-1 py-0.5" title="행사 시작까지 남은 일수">D-{dday}</div>
 ) : null}
 {video.thumbnail_path ? (
 <img
 src={`/uploads/thumbnails/${video.thumbnail_path}`}
 alt={video.title}
 className="w-full h-24 object-cover"
 />
 ) : (
 <div className="w-full h-24 bg-gray-200 flex items-center justify-center">
 <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
 </svg>
 </div>
 )}
 <div className="mt-1 text-xs truncate">{video.title}</div>
 {slotCounts[video.file_hash] > 0 && (
 <div className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-1 py-0.5">
 {slotCounts[video.file_hash]}개 슬롯
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}
 </div>
 </div>

 {/* 기간 및 예산 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">기간 및 예산</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 시작 날짜 및 시각 <span className="text-red-500">*</span>
 </label>
 <input
 type="datetime-local"
 value={formData.startDatetime}
 onChange={(e) => setFormData({ ...formData, startDatetime: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 required
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 종료 날짜 및 시각 <span className="text-red-500">*</span>
 </label>
 <input
 type="datetime-local"
 value={formData.endDatetime}
 onChange={(e) => setFormData({ ...formData, endDatetime: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 required
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 예산 (원) <span className="text-red-500">*</span>
 </label>
 <input
 type="number"
 value={formData.budget}
 onChange={(e) => setFormData({ ...formData, budget: parseInt(e.target.value) || 0 })}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 min="0"
 required
 />
 </div>
 </div>
 </div>

 {/* 계산된 값 표시 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">캠페인 통계 (자동 계산)</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="bg-gray-50 p-4">
 <div className="text-sm text-gray-600">현재 영상을 재생 중인 슬롯 수</div>
 <div className="text-2xl font-bold text-gray-900">{totalSlots}개</div>
 </div>
 
 <div className="bg-gray-50 p-4">
 <div className="text-sm text-gray-600">현재 영상을 재생 중인 스크린 수</div>
 <div className="text-2xl font-bold text-gray-400">집계예정</div>
 </div>
 </div>
 </div>

 {/* 버튼 */}
 <div className="flex justify-end gap-3 pt-6">
 <button
 type="button"
 onClick={() => navigate('/campaign')}
 className="px-4 py-2 border border-gray-300 hover:bg-gray-50"
 >
 취소
 </button>
 <button
 type="submit"
 disabled={isLoading}
 className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
 >
 {isLoading ? '저장 중...' : (isEdit ? '수정' : '생성')}
 </button>
 </div>
 </form>
 </div>
 );
};

export default CampaignForm;